import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Send, Users, Radio, Settings, Trash2, Ban, Power, AlertTriangle, UserX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// COMPONENTS DEFINED OUTSIDE - CRITICAL!
// This prevents React from recreating them
// ============================================

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl ${className}`}>
    {children}
  </div>
);

const ChatInput = ({ value, onChange, placeholder, onKeyDown, ...props }) => (
  <textarea
    value={value}
    onChange={onChange}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    className="flex-1 h-10 rounded-md border px-3 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-black/50 border-white/20 resize-none"
    autoComplete="off"
    spellCheck="false"
    rows={1}
    {...props}
  />
);

const NicknameInput = ({ value, onChange, ...props }) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    placeholder="Your nickname"
    className="flex h-10 w-full rounded-md border px-3 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-black/50 border-white/20"
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

// ============================================
// MAIN COMPONENT
// ============================================

const Chat = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  
  // State
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [nickname, setNickname] = useState('');
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const messagesEndRef = useRef(null);
  const chatPollRef = useRef(null);
  
  // Owner settings state
  const [showSettings, setShowSettings] = useState(false);
  const [chatPageEnabled, setChatPageEnabled] = useState(true);
  const [radioPageEnabled, setRadioPageEnabled] = useState(true);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);

  const isOwner = user?.role === 'owner' || user?.role === 'admin';

  // Initial data fetch
  useEffect(() => {
    fetchConfig();
    fetchMessages();
    fetchChatConfig();

    // Poll for new messages - but only update if changed
    chatPollRef.current = setInterval(fetchMessages, 5000);
    
    return () => {
      if (chatPollRef.current) clearInterval(chatPollRef.current);
    };
  }, []);

  // Set nickname from user or localStorage
  useEffect(() => {
    if (user) {
      setNickname(user.username);
    } else {
      const savedNickname = localStorage.getItem('beatlab_nickname');
      if (savedNickname) {
        setNickname(savedNickname);
      }
    }
  }, [user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/radio/config`);
      setChatEnabled(res.data.chat_enabled ?? true);
      setIsLive(res.data.is_live ?? false);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchChatConfig = async () => {
    try {
      const res = await axios.get(`${API}/chat/config`);
      setChatPageEnabled(res.data.chat_page_enabled ?? true);
      setRadioPageEnabled(res.data.radio_page_enabled ?? true);
    } catch (error) {
      console.error('Error fetching chat config:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?limit=100&source=chat`);
      const newMsgs = res.data;
      // Only update if messages actually changed
      setMessages(prev => {
        if (prev.length !== newMsgs.length) return newMsgs;
        if (prev.length > 0 && newMsgs.length > 0) {
          if (prev[prev.length - 1]?.id !== newMsgs[newMsgs.length - 1]?.id) {
            return newMsgs;
          }
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchBannedUsers = async () => {
    try {
      const token = localStorage.getItem('beatlab_token');
      const res = await axios.get(`${API}/chat/banned-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBannedUsers(res.data);
    } catch (error) {
      console.error('Error fetching banned users:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!nickname.trim()) {
      setShowNicknamePrompt(true);
      return;
    }

    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.post(
        `${API}/chat/messages?source=chat`,
        { nickname, message: newMessage },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Chat is disabled');
      } else {
        toast.error('Failed to send message');
      }
    }
  };

  const handleSetNickname = () => {
    if (nickname.trim()) {
      setShowNicknamePrompt(false);
      localStorage.setItem('beatlab_nickname', nickname);
    }
  };

  // Owner actions
  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleBanUser = async (userNickname) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.post(`${API}/chat/ban/${userNickname}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userNickname} has been banned`);
      fetchMessages();
      fetchBannedUsers();
    } catch (error) {
      toast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userNickname) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/ban/${userNickname}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userNickname} has been unbanned`);
      fetchBannedUsers();
    } catch (error) {
      toast.error('Failed to unban user');
    }
  };

  const handleRemoveUserMessages = async (userNickname) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/user/${userNickname}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`All messages from ${userNickname} removed`);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to remove messages');
    }
  };

  const handleClearChat = async () => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.delete(`${API}/chat/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All chat messages cleared');
      setConfirmClear(false);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to clear chat');
    }
  };

  const handleUpdateChatConfig = async (chatPage, radioPage) => {
    try {
      const token = localStorage.getItem('beatlab_token');
      await axios.put(`${API}/chat/config`, {
        chat_page_enabled: chatPage,
        radio_page_enabled: radioPage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatPageEnabled(chatPage);
      setRadioPageEnabled(radioPage);
      toast.success('Chat settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const openSettings = () => {
    fetchBannedUsers();
    setShowSettings(true);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="py-4">
      <div className="max-w-4xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 
              className="text-xl sm:text-2xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
              style={{ color: theme.primary, textShadow: theme.glow }}
              data-testid="chat-title"
            >
              <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7" />
              Chat Room
            </h1>
            <p className="text-sm text-gray-400">Connect with the community</p>
          </div>
          
          <div className="flex items-center gap-2">
            {isOwner && (
              <Button
                variant="outline"
                size="icon"
                onClick={openSettings}
                className="relative"
                style={{ borderColor: theme.primary, color: theme.primary }}
                data-testid="chat-settings-btn"
              >
                <Settings className="w-5 h-5" />
              </Button>
            )}
            
            {isLive && (
              <Link to="/radio">
                <Button
                  className="flex items-center gap-2"
                  style={{ backgroundColor: theme.primary, color: '#000' }}
                >
                  <Radio className="w-4 h-4" />
                  DJ808 is Live!
                </Button>
              </Link>
            )}
          </div>
        </div>

        <GlassCard className="h-[70vh] flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5" style={{ color: theme.primary }} />
              <span className="text-sm text-gray-400">{messages.length} messages</span>
            </div>
            <div className="flex items-center gap-3">
              {!chatPageEnabled && (
                <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400">Disabled</span>
              )}
              {chatPageEnabled && chatEnabled && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs text-gray-500">Live</span>
                </div>
              )}
            </div>
          </div>

          {chatEnabled && chatPageEnabled ? (
            <>
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="flex gap-3 group"
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                        style={{ 
                          backgroundColor: `${theme.primary}20`,
                          color: theme.primary,
                        }}
                      >
                        {msg.nickname.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold" style={{ color: theme.primary }}>{msg.nickname}</span>
                          <span className="text-xs text-gray-600">{formatTime(msg.created_at)}</span>
                          
                          {isOwner && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 ml-2">
                              <button
                                onClick={() => handleDeleteMessage(msg.id)}
                                className="p-1 hover:bg-red-500/20 rounded text-red-400"
                                title="Delete message"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleBanUser(msg.nickname)}
                                className="p-1 hover:bg-yellow-500/20 rounded text-yellow-400"
                                title="Ban user"
                              >
                                <Ban className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleRemoveUserMessages(msg.nickname)}
                                className="p-1 hover:bg-orange-500/20 rounded text-orange-400"
                                title="Remove all messages from user"
                              >
                                <UserX className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-300 break-words">{msg.message}</p>
                      </div>
                    </motion.div>
                  ))}
                  {messages.length === 0 && (
                    <div className="text-center py-16">
                      <MessageSquare className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                      <p className="text-gray-500">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input Area */}
              {showNicknamePrompt && !user ? (
                <div className="p-4 border-t border-white/10">
                  <p className="text-sm text-gray-400 mb-3">Enter a nickname to chat:</p>
                  <div className="flex gap-2">
                    <NicknameInput
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      data-testid="nickname-input"
                    />
                    <Button 
                      onClick={handleSetNickname}
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      data-testid="set-nickname-btn"
                    >
                      Set Nickname
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                      style={{ 
                        backgroundColor: `${theme.primary}20`,
                        color: theme.primary,
                      }}
                    >
                      {nickname ? nickname.charAt(0).toUpperCase() : '?'}
                    </div>
                    <ChatInput
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={`Message as ${nickname || 'Guest'}...`}
                      maxLength={500}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit"
                      style={{ backgroundColor: theme.primary, color: '#000' }}
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  {!nickname && !user && (
                    <button
                      type="button"
                      onClick={() => setShowNicknamePrompt(true)}
                      className="text-xs mt-2 ml-12 hover:underline"
                      style={{ color: theme.secondary }}
                    >
                      Add Nickname
                    </button>
                  )}
                </form>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-500 text-lg">Chat is currently disabled</p>
                <p className="text-gray-600 text-sm mt-2">Check back during live sessions!</p>
              </div>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Owner Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-black/95 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.primary }}>
              <Settings className="w-5 h-5" />
              Chat Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Enable/Disable Chat */}
            <div className="space-y-4 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                <Power className="w-4 h-4" /> Chat Controls
              </h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Chat Page</p>
                  <p className="text-xs text-gray-500">Enable chat on /chat</p>
                </div>
                <Switch
                  checked={chatPageEnabled}
                  onCheckedChange={(checked) => handleUpdateChatConfig(checked, radioPageEnabled)}
                  data-testid="chat-page-toggle"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">Radio Page Chat</p>
                  <p className="text-xs text-gray-500">Enable chat on /radio</p>
                </div>
                <Switch
                  checked={radioPageEnabled}
                  onCheckedChange={(checked) => handleUpdateChatConfig(chatPageEnabled, checked)}
                  data-testid="radio-chat-toggle"
                />
              </div>
            </div>

            {/* Clear Chat */}
            <div className="space-y-3 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Clear Chat
              </h3>
              
              {!confirmClear ? (
                <Button
                  variant="outline"
                  onClick={() => setConfirmClear(true)}
                  className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
                  data-testid="clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Messages
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-400 text-xs">
                    <AlertTriangle className="w-4 h-4" />
                    This will delete ALL chat messages. Are you sure?
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setConfirmClear(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleClearChat}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      data-testid="confirm-clear-btn"
                    >
                      Yes, Clear All
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Banned Users */}
            <div className="space-y-3 p-4 rounded-lg bg-white/5">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                <Ban className="w-4 h-4" /> Banned Users ({bannedUsers.length})
              </h3>
              
              {bannedUsers.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {bannedUsers.map((nick) => (
                    <div key={nick} className="flex items-center justify-between p-2 bg-white/5 rounded">
                      <span className="text-sm text-gray-300">{nick}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUnbanUser(nick)}
                        className="text-green-400 hover:bg-green-500/20 h-7 px-2"
                      >
                        Unban
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No banned users</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
